import { createSentryPiniaPlugin } from '@sentry/vue';
import { createApp, markRaw } from 'vue';
import { createPinia } from 'pinia';
import { PiniaColada, PiniaColadaQueryHooksPlugin } from '@pinia/colada';
import { globalErrors } from '@/composables/useErrors';
import LaravelError from '@/api/model/LaravelError';
import App from './App.vue';

// Browser-level fetch failures (aborted requests, navigation tear-down,
// server unreachable) all surface as either our LaravelError wrapper with a
// ERR_CANCELED/ERR_NETWORK code or as a raw TypeError with "Failed to fetch".
function isTransportError(error: unknown): boolean {
    if (error instanceof LaravelError) {
        const code = error.localCause?.code;
        return code === 'ERR_CANCELED' || code === 'ERR_NETWORK';
    }
    return error instanceof TypeError && /Failed to fetch|NetworkError|Load failed/.test(error.message);
}

// Meta shape queries can attach for centralised toast handling.
// Read by the Pinia Colada plugin hook below.
type ToastMeta = {
    errorMessage?: string;
    errorCategory?: string;
};

// You can use the following starter router instead of the default one as a clean starting point
// import router from "./router/starter";
import router from '@/router';

// Main Stylesheet
import '@/../scss/main.scss';

// Template components
import BaseBlock from '@/components/BaseBlock.vue';
import BaseBackground from '@/components/BaseBackground.vue';
import BasePageHeading from '@/components/BasePageHeading.vue';

// Template directives
import clickRipple from '@/directives/clickRipple';

import { configureEcho, echo } from '@laravel/echo-vue';
import { configureApiFetch } from '@/lib/http';
import { useAuthStore } from '@/stores/authStore';

configureEcho({
    broadcaster: 'reverb',
});

// Craft new application
const app = createApp(App);

// Register global components
app.component('BaseBlock', BaseBlock);
app.component('BaseBackground', BaseBackground);
app.component('BasePageHeading', BasePageHeading);

// Register global directives
app.directive('click-ripple', clickRipple);

import Swal from 'sweetalert2';
window.Swal = Swal;

// Use Pinia and Vue Router
const pinia = createPinia();
pinia.use(createSentryPiniaPlugin());
pinia.use(({ store }) => {
    store.router = markRaw(router);
});
app.use(pinia);
app.use(PiniaColada, {
    plugins: [
        // Centralised toast for queries: any query that declares meta.errorMessage
        // gets a toast on failure. Mutations keep their own onSuccess/onError
        // because their messages are tightly coupled to specific user actions.
        PiniaColadaQueryHooksPlugin({
            onError(error, entry) {
                const meta = entry.options?.meta as ToastMeta | undefined;
                if (!meta?.errorMessage) {
                    return;
                }
                // Skip toasting transport errors that fire while the page is
                // being torn down: a full navigation (page.goto in Playwright,
                // external link, reload) flips document.hidden to true and
                // then rejects every in-flight fetch as "Failed to fetch".
                // There is no one left to read a toast in that case, and the
                // browser's own error overlay already surfaces genuine
                // connectivity failures that happen while the tab is visible.
                if (document.hidden && isTransportError(error)) {
                    return;
                }
                // Same idea for SPA-internal navigations: if pinia-colada has
                // already released every subscriber for this query by the
                // time the error fires, the data isn't going anywhere useful.
                if (entry.deps?.size === 0) {
                    return;
                }
                const errors = globalErrors();
                if (!errors) {
                    // setupGlobalErrors() hasn't run yet (e.g. a query failed before
                    // App.vue mounted GlobalErrorsInit). Swallow rather than throw
                    // so the original error isn't masked.
                    return;
                }
                errors.showErrors(error, meta.errorMessage, meta.errorCategory);
            },
        }),
    ],
});
app.use(router);

let isHandlingUnauthorized = false;

configureApiFetch({
    getSocketId: () => {
        try {
            return echo().socketId();
        } catch {
            return null;
        }
    },
    async onUnauthorized(context) {
        if (context.url.includes('/api/users/me') || isHandlingUnauthorized) {
            return;
        }

        isHandlingUnauthorized = true;
        const authStore = useAuthStore();
        try {
            await authStore.logout();
            router.push({ name: 'login' });
        } finally {
            isHandlingUnauthorized = false;
        }
    },
    onCsrfMismatch() {
        const authStore = useAuthStore();
        authStore.setLoggedOut();
        router.push({ name: 'login' });
    },
});

// ..and finally mount it!
app.mount('#app');

import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';

import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    TimeScale,
    Title,
    Tooltip,
} from 'chart.js';
import 'chartjs-adapter-luxon';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    ArcElement,
    LineElement,
    PointElement,
    TimeScale,
    Filler,
    annotationPlugin,
);

import * as Sentry from '@sentry/vue';

if (import.meta.env['VITE_SENTRY_DSN_PUBLIC']) {
    const sensitiveKeys =
        /password|secret|token|authorization|cookie|csrf|xsrf|api_key|private_key|session|bearer|credential/i;

    function stripSensitiveData(obj: Record<string, unknown>): Record<string, unknown> {
        const cleaned: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(obj)) {
            if (sensitiveKeys.test(key)) {
                cleaned[key] = '[Filtered]';
            } else if (value && typeof value === 'object' && !Array.isArray(value)) {
                cleaned[key] = stripSensitiveData(value as Record<string, unknown>);
            } else {
                cleaned[key] = value;
            }
        }
        return cleaned;
    }

    Sentry.init({
        app,
        dsn: import.meta.env['VITE_SENTRY_DSN_PUBLIC'] as string,
        beforeSend(event) {
            if (event.request?.data && typeof event.request.data === 'object') {
                event.request.data = stripSensitiveData(event.request.data as Record<string, unknown>);
            }
            if (event.request?.headers) {
                event.request.headers = stripSensitiveData(event.request.headers as Record<string, unknown>) as Record<
                    string,
                    string
                >;
            }

            // Check if it is an exception, and if so, show the report dialog
            if (event.exception) {
                Sentry.showReportDialog({ eventId: event.event_id });
            }
            return event;
        },
        beforeBreadcrumb(breadcrumb) {
            if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
                if (breadcrumb.data && typeof breadcrumb.data === 'object') {
                    for (const key of Object.keys(breadcrumb.data)) {
                        if (sensitiveKeys.test(key)) {
                            breadcrumb.data[key] = '[Filtered]';
                        }
                    }
                }
            }
            return breadcrumb;
        },
        integrations: [
            Sentry.browserTracingIntegration({
                router: router,
            }),
            Sentry.vueIntegration({
                tracingOptions: {
                    trackComponents: true,
                },
            }),
        ],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: (import.meta.env['VITE_SENTRY_TRACES_SAMPLE_RATE'] as number) ?? 0.2,
    });
}
